import React, { useState, useEffect } from "react";
import { Trash2, SquarePen, Search } from "lucide-react";
import Pagination from "../utils/pagination/Pagination";
import { toast } from "react-toastify";

import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  TableContainer,
  Checkbox,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@chakra-ui/react";

import ProductReviewForm from "./ProductReviewForm";
import ReviewSearch from "./ReviewSearch";
import { deleteProductReviewApi, getProductReviewApi } from "../../../redux/reduxApi/admin/ProductReviewApi";
import { deleteProductReviewAction } from "../../../redux/reduxSlice/admin/ProductReviewSlice";
import StatusBadge from "../../../custom/StatusBedge";
import { reviewStatus } from "../utils/SearchPickList";

function ProductReviewList() {
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [updatedData, setUpdatedData] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useDispatch();
  const ProductReviewData = useSelector((state) => state.AdminProductReview.getProductReview);

  const currentItems = ProductReviewData;

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  const openDrawer = (item) => {
    setUpdatedData(item);
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => setIsDrawerOpen(false);

  const openDeleteModal = (rowid) => {
    if (rowid) setDeleteMaterialId(rowid);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteMaterialId(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(getProductReviewApi({ page: currentPage, limit: 30 }));
        console.log("product review res",response)
        setLoading(false);
        setRecordCount(response.payload.Total);
      } catch (error) {
        console.error("Error fetching product review data: ", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, currentPage, isDrawerOpen]);

  const handleRowSelect = (id) => {
    const isSelected = selectedRows.includes(id);
    const updatedRows = isSelected
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];
    setSelectedRows(updatedRows);
  };

  const handleSelectAllRows = () => {
    if (selectedRows.length === currentItems?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentItems.map((item) => item.ROWID));
    }
  };

  const handleDelete = async () => {
    const idsToDelete = deleteMaterialId ? [deleteMaterialId] : selectedRows;
    if (idsToDelete.length === 0) {
      closeDeleteModal();
      return;
    }

    setIsDeleting(true);
    try {
      const deletePromises = idsToDelete.map(id =>
        dispatch(deleteProductReviewApi(id))
      );
      await Promise.all(deletePromises);

      dispatch(deleteProductReviewAction(idsToDelete));
      const updatedData = ProductReviewData.filter((item) => !idsToDelete.includes(item.ROWID));
      setRecordCount(updatedData.length);
      setSelectedRows([]);
      setDeleteMaterialId(null);

      toast.success(`${idsToDelete.length} product review(s) deleted successfully`);
    } catch (error) {
      console.error("Error deleting product reviews:", error);
      toast.error("Failed to delete product review(s)");
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to Delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody />
          <ModalFooter>
            <Button
              className="main-btn"
              mr={3}
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeDeleteModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Toolbar */}
      <div className="d-flex justify-content-between mb-1 px-2 items-center">
        <div>
          {selectedRows.length > 0 && (
            <button className="main-btn mx-2" onClick={() => openDeleteModal()}>
              <span className="inner-content">
                Delete All ({selectedRows.length})
              </span>
            </button>
          )}
        </div>
        <div className="d-flex justify-content-end">
          <button className="main-btn mx-2" onClick={openSearch}>
            <span className="inner-content">
              <Search size="18px" />
              <span className="ms-1">Search</span>
            </span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="custom-scroll" style={{ height: "80vh" }}>
        <TableContainer
          className="card card-detail mt-1 border-0 m-1"
          style={{overflowY: "auto" }}
        >
          <Table id="table-to-xls" size="sm">
            <Thead>
              <Tr className="tr-border">
                <Th className="py-1 th-color">
                  <Checkbox
                    isChecked={selectedRows.length === currentItems?.length}
                    onChange={handleSelectAllRows}
                  />
                </Th>
                <Th className="py-1 text-black fw-bold">Actions</Th>
                <Th className="py-1 text-black fw-bold">Customer Name</Th>
                <Th className="py-1 text-black fw-bold">Rating</Th>
                <Th className="py-1 text-black fw-bold">Status</Th>
                <Th className="py-1 text-black fw-bold">Review</Th>
                <Th className="py-1 text-black fw-bold">Created By</Th>
                <Th className="py-1 text-black fw-bold">Modified By</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ProductReviewData?.map((item) => (
                <Tr className="tr-border" key={item.ROWID}>
                  <Td className="font-colors">
                    <Checkbox
                      isChecked={selectedRows.includes(item.ROWID)}
                      onChange={() => handleRowSelect(item.ROWID)}
                    />
                  </Td>
                  <Td>
                    <div className="d-flex gap-1">
                      <i onClick={() => openDrawer(item)} style={{ cursor: "pointer" }}>
                        <SquarePen color="#011638" size="16px" strokeWidth="1" />
                      </i>
                      <i
                        style={{ cursor: "pointer" }}
                        onClick={() => openDeleteModal(item.ROWID)}
                      >
                        <Trash2 color="#011638" size="16px" strokeWidth="1" />
                      </i>
                    </div>
                  </Td>
                  <Td className="font-colors">{`${item.webuser?.firstName} ${item.webuser?.lastName}`}</Td>
                  <Td className="font-colors">{item.rating}</Td>
                  <Td><StatusBadge options={reviewStatus} value={item.status} /></Td>
                  <Td
                    className="font-colors"
                    maxW="200px"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    <Tooltip
                      label={item.review}
                      hasArrow
                      placement="bottom"
                      openDelay={300}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: "200px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                        }}
                      >
                        {item.review}
                      </span>
                    </Tooltip>
                  </Td>
                  <Td className="font-colors">
                    {item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : "-"}
                  </Td>
                  <Td className="font-colors">
                    {item.modifiedBy ? `${item.modifiedBy.firstName} ${item.modifiedBy.lastName}` : "-"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Footer */}
      <div className="footer d-flex fixed-bottom start-20">
        <div className="total" style={{ marginLeft: "20%", marginTop: "15px" }}>
          <p className="fw-semibold">Total Count: {recordCount}</p>
        </div>
        <div className="w-full flex items-center justify-center fixed bottom-0 mx-auto">
          <Pagination
            totalPages={Math.ceil(recordCount / 30)}
            onPageChange={setCurrentPage}
            setRecordCount={setRecordCount}
          />
        </div>
      </div>

      {/* Search and Form Drawers */}
      <ReviewSearch closeDrawer={closeSearch} isSearchDrawerOpen={isSearchOpen} />
      <ProductReviewForm
        closeDrawer={closeDrawer}
        isDrawerOpen={isDrawerOpen}
        updatedData={updatedData}
      />
    </>
  );
}

export default ProductReviewList;